import React from 'react';
import { useSelector } from 'react-redux';
import { Alert, Radio } from 'antd';
import { getLang } from 'shared/store/selectors/lang.selector';
import { UPLOAD_MODE, type FailedRow, type UploadMode } from '../contacts-import';
import styles from './upload-mode-section.module.scss';

type UploadModeSectionProps = {
  value: UploadMode | null;
  onChange: (mode: UploadMode) => void;
  replaceErrors: FailedRow[];
  disabled?: boolean;
};

/**
 * The Add / Replace choice of the contacts import, with the rows that blocked a Replace.
 * Nothing is selected until the admin picks a mode.
 *
 * @param props.value - The chosen mode, or `null` before one is picked.
 * @param props.onChange - Called with the picked mode.
 * @param props.replaceErrors - Rows the Replace dry run rejected; empty hides the alert.
 * @param props.disabled - Locks the choice while a submit is in flight, so a Replace preview can't
 *   come back under Add. Default `false`.
 * @returns The mode fieldset and, when a Replace was blocked, its errors.
 */
export const UploadModeSection = ({
  value,
  onChange,
  replaceErrors,
  disabled = false,
}: UploadModeSectionProps) => {
  const lang = useSelector(getLang('SETTINGS'));

  const options = [
    {
      value: UPLOAD_MODE.ADD,
      label: lang.UPLOAD_MODE_ADD,
      description: lang.UPLOAD_MODE_ADD_DESCRIPTION,
    },
    {
      value: UPLOAD_MODE.REPLACE,
      label: lang.UPLOAD_MODE_REPLACE,
      description: `${lang.UPLOAD_MODE_REPLACE_DESCRIPTION} ${lang.REPLACE_KEEPS_USERS}`,
    },
  ];

  return (
    <>
      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>{lang.UPLOAD_MODE_TITLE}</legend>
        <Radio.Group
          value={value}
          disabled={disabled}
          onChange={e => onChange(e.target.value)}
          className={styles.options}>
          {options.map(option => (
            <Radio key={option.value} value={option.value}>
              <span className={styles.label}>{option.label}</span>
              <span className={styles.description}>{option.description}</span>
            </Radio>
          ))}
        </Radio.Group>
        {!value && <p className={styles.hint}>{lang.UPLOAD_MODE_REQUIRED}</p>}
      </fieldset>
      {replaceErrors.length > 0 && (
        <Alert
          type="error"
          showIcon
          message={lang.REPLACE_BLOCKED_TITLE}
          description={
            <>
              <p>{lang.REPLACE_BLOCKED_DESCRIPTION}</p>
              <ul className={styles.errorList}>
                {replaceErrors.map((row, index) => (
                  <li key={index}>
                    {[row.firstName, row.email].filter(Boolean).join(' ')} — {row.message}
                  </li>
                ))}
              </ul>
            </>
          }
        />
      )}
    </>
  );
};
